import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { productSchema, breadcrumb } from "@/i18n/schemaData";
import { useParams, Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Wifi, Bluetooth,
  Signal, Globe, Monitor, Printer, Battery, Cpu, HardDrive,
  Smartphone, Shield, CreditCard
} from "lucide-react";
import { TERMINALS_DATA, TERMINAL_SLUGS } from "@/data/terminals";
import { useTranslation } from "@/i18n/translations";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function TerminalDetailPage() {
  const { slug } = useParams();
  const terminal = TERMINALS_DATA[slug];
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!terminal) {
    return (
      <div className="pt-32 pb-20 text-center">
        <h1 className="text-2xl font-bold text-[#0A1128]">Terminal not found</h1>
        <Link to="/solutions" className="text-[#00C853] mt-4 inline-block">{t("terminal.back")}</Link>
      </div>
    );
  }

  const currentIdx = TERMINAL_SLUGS.indexOf(slug);
  const prevSlug = currentIdx > 0 ? TERMINAL_SLUGS[currentIdx - 1] : null;
  const nextSlug = currentIdx < TERMINAL_SLUGS.length - 1 ? TERMINAL_SLUGS[currentIdx + 1] : null;

  const specIcons = {
    dimensions: Monitor,
    poids: Shield,
    ecran: Smartphone,
    imprimante: Printer,
    batterie: Battery,
    processeur: Cpu,
    memoire: HardDrive,
    os: Monitor,
    camera: Smartphone,
    pci: Shield,
    p2pe: Shield
  };

  const specLabels = {
    dimensions: t("terminal.dimensions"),
    poids: t("terminal.weight"),
    ecran: t("terminal.screen"),
    imprimante: t("terminal.printer"),
    batterie: t("terminal.battery"),
    processeur: t("terminal.processor"),
    memoire: t("terminal.memory"),
    os: t("terminal.os"),
    camera: t("terminal.camera"),
    pci: t("terminal.pci"),
    p2pe: t("terminal.p2pe")
  };

  return (
    <div data-testid={`terminal-page-${slug}`}>
      <SEO page="tinfo" />
      <JsonLd data={[
        productSchema(terminal),
        breadcrumb([
          { name: "Accueil", path: "/" },
          { name: "Terminaux", path: "/terminaux-de-paiement" },
          { name: terminal.name, path: `/terminaux/${slug}` },
        ]),
      ]} />
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-36 lg:pb-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/solutions" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#1B2B5B] mb-6 transition-colors">
            <ArrowLeft size={14} /> {t("terminal.back")}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00C853] mb-3">
                {terminal.category}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1128] tracking-tight leading-[1.1]">
                {terminal.name}
              </h1>
              <p className="text-lg text-[#475569] mt-4 font-medium">{terminal.tagline}</p>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">{terminal.description}</p>
              <div className="mt-8 p-5 bg-white rounded-xl border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#00C853]/10 flex items-center justify-center">
                    <CheckCircle2 size={16} className="text-[#00C853]" />
                  </div>
                  <span className="text-sm font-semibold text-[#0A1128]">{t("terminal.included")}</span>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed">
                  {t("terminal.includedDesc")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link
                  to="/contact"
                  data-testid="terminal-cta-demo"
                  className="inline-flex items-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all"
                >
                  {t("terminal.askTerminal")}
                </Link>
                <Link
                  to="/tarifs"
                  className="inline-flex items-center font-semibold border border-[#1B2B5B] text-[#1B2B5B] rounded-full px-7 py-3 text-sm hover:bg-[#1B2B5B] hover:text-white transition-all"
                >
                  {t("terminal.seeOffer")}
                </Link>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 flex items-center justify-center">
                <img src={terminal.image} alt={terminal.name} loading="lazy" className="h-72 lg:h-96 object-contain" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {terminal.highlights.map((h, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="feature-card p-6 bg-[#F8F9FA] rounded-xl border border-slate-100 h-full">
                  <CheckCircle2 size={20} className="text-[#00C853] mb-3" />
                  <p className="text-sm text-[#475569] leading-relaxed">{h}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Lifestyle image */}
      {terminal.lifestyle && (
        <section className="py-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src={terminal.lifestyle} alt={`${terminal.name} en utilisation`} loading="lazy" className="w-full h-64 lg:h-96 object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* Technical specs */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("terminal.specsTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {t("terminal.specsTitle")}
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Hardware specs */}
            <AnimatedSection>
              <h3 className="text-lg font-semibold text-[#0A1128] mb-5">{t("terminal.hardware")}</h3>
              <div className="space-y-4">
                {Object.entries(terminal.specs).map(([key, value]) => {
                  const Icon = specIcons[key] || Monitor;
                  return (
                    <div key={key} className="flex items-start gap-4 pb-4 border-b border-slate-100 last:border-0">
                      <div className="w-9 h-9 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-[#1B2B5B]" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-medium text-[#475569] uppercase tracking-wide">{specLabels[key] || key}</div>
                        <div className="text-sm text-[#0A1128] mt-0.5">{value}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AnimatedSection>

            {/* Connectivity + payments */}
            <div>
              <AnimatedSection delay={0.1}>
                <h3 className="text-lg font-semibold text-[#0A1128] mb-5">{t("terminal.connectivity")}</h3>
                <div className="grid grid-cols-2 gap-3 mb-10">
                  {[
                    { name: "Ethernet", available: terminal.connectivity.ethernet, icon: Globe },
                    { name: "4G/5G", available: terminal.connectivity.cellular, icon: Signal },
                    { name: "WiFi", available: terminal.connectivity.wifi, icon: Wifi },
                    { name: "Bluetooth", available: terminal.connectivity.bluetooth, icon: Bluetooth },
                  ].map((c, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        c.available ? "border-[#00C853]/20 bg-[#00C853]/5" : "border-slate-100 bg-slate-50 opacity-50"
                      }`}
                    >
                      <c.icon size={16} className={c.available ? "text-[#00C853]" : "text-slate-300"} />
                      <span className={`text-sm ${c.available ? "text-[#0A1128]" : "text-slate-400"}`}>{c.name}</span>
                    </div>
                  ))}
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <h3 className="text-lg font-semibold text-[#0A1128] mb-5">{t("terminal.payments")}</h3>
                <div className="space-y-2.5">
                  {terminal.payments.map((p, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CreditCard size={14} className="text-[#00C853] shrink-0" />
                      <span className="text-sm text-[#475569]">{p}</span>
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* AyaPay included banner */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="bg-[#1B2B5B] rounded-2xl p-8 lg:p-12 text-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl lg:text-3xl font-bold">{t("terminal.allIncluded")}</h3>
                  <p className="text-slate-300 mt-3 text-sm leading-relaxed">
                    {t("terminal.allIncludedDesc")}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    t("pricing.insurance"),
                    t("pricing.maintenance"),
                    t("pricing.support247"),
                    t("pricing.sim"),
                    t("pricing.replace"),
                    t("pricing.updates")
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#00C853] shrink-0" />
                      <span className="text-sm text-slate-200">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {prevSlug ? (
            <Link
              to={`/terminaux/${prevSlug}`}
              className="flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#1B2B5B] transition-colors"
            >
              <ArrowLeft size={16} />
              {TERMINALS_DATA[prevSlug].name}
            </Link>
          ) : <div />}
          {nextSlug ? (
            <Link
              to={`/terminaux/${nextSlug}`}
              className="flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#1B2B5B] transition-colors"
            >
              {TERMINALS_DATA[nextSlug].name}
              <ArrowRight size={16} />
            </Link>
          ) : <div />}
        </div>
      </section>
    </div>
  );
}
